<template>
    <div class="p-0">
      <div class="row mb-5 mx-0">
        <div class="col-12">
          <div v-if="canViewEmptyMessage" class="tag-badge custom-tag-badge">
            <span class="dot custom-dot text-center pl-1">
              <em
                class="orange-icon orange-icon-badge-align fas fa-exclamation-circle"
              />
            </span>
            <span class="tag-badge-title py-1 pl-2 at-badge">Alertas</span>
          </div>
          <div v-if="alerts.length > 0" class="row">
            <div class="col">
              <div class="mt-5">
                <custom-carousel
                  :items="alerts"
                  :size-per-slide="slideSize"
                  @go-to-details="goToDetailsHandler"
                />
              </div>
            </div>
          </div>
          <div
            v-if="alerts.length == 0 && canViewEmptyMessage"
            class="alert-empty-container"
          >
            <div class="alert-empty-container__wrapper">
              <em class="fal fa-unlink" />
              <span>Actualmente no cuenta con alertas en sistema.</span>
            </div>
          </div>
        </div>
      </div>
      <div class="row mx-0">
        <div class="col-12 col-md-4">
          <inbox
            ref="_messagesInbox"
            :get-messages="getInboxMessages"
            :download-document="downloadDocument"
          />
        </div>
        <div class="col-12 col-md-8">
          <compact-calendar :items="mappedSchedule" @onDayChange="onDayChange" />
        </div>
      </div>
      <immediate-message-popup
        id="immediateMessagePopup"
        ref="_immediateMessagePopupRef"
        :items="immediateMessages"
        @message-marked-as-read="onMarkedAsRead"
      />
    </div>
  </template>
  
  <script>
  import { modulesName } from '@/modules';
  import { createNamespacedHelpers } from 'vuex';
  import CustomCarousel from 'custom-carousel';
  import { ImmediateMessagePopup } from '@/components/dashboard/inbox';
  import sessionManager from '@/utils/managers/sessionManager';
  import sessionKeys from '@/utils/constants/sessionKeys.js';
  const { mapGetters, mapMutations, mapActions } = createNamespacedHelpers(
    modulesName.dashboardModuleName
  );
  import { Inbox, CompactCalendar } from 'operations';
  import _generalService from '@/services/pei/general';
  import _service from '@/modules/dashboard/api.js';
  import moment from 'moment';
  import _ from 'lodash';
  import locationMessageCode from '@/utils/constants/messageLocationCode.js';
  
  //default size per slide
  const DEFAULT_SIZE_PER_SLIDE_DESKTOP = 4;
  const DEFAULT_SIZE_PER_SLIDE_MOBILE = 1;
  const HOUR_FORMAT_AM_PM = 'hh:mm A';
  
  export default {
    name: 'Dashboard',
    components: {
      CustomCarousel,
      Inbox,
      ImmediateMessagePopup,
      CompactCalendar,
    },
    data() {
      return {
        immediateMessages: [],
        inboxMessages: [],
      };
    },
    computed: {
      ...mapGetters({
        alerts: 'getAlerts',
        hasMoreMessages: 'getMessagesHasShowMore',
        schedule: 'getSchedule',
      }),
      mappedSchedule() {
        return this.schedule.map((item) => {
          const i = { ...item };
          try {
            i.iconRef = require('@/assets/img/' + i.iconRef);
          } catch {
            //Only if image is not available
            i.iconRef = require('@/assets/img/clock.svg');
          }
          i.hourStart = moment(i.hourStartFormatted, HOUR_FORMAT_AM_PM)
            .format(HOUR_FORMAT_AM_PM)
            .toLowerCase();
  
          return i;
        });
      },
      isMobile() {
        return this.$store.getters.getIsMobileDensity;
      },
      slideSize() {
        return this.isMobile
          ? DEFAULT_SIZE_PER_SLIDE_MOBILE
          : DEFAULT_SIZE_PER_SLIDE_DESKTOP;
      },
      canViewEmptyMessage() {
        return this.$store.getters.HasPermission(
          'menu.dash.alert.viewemptymessage'
        );
      },
    },
    methods: {
      ...mapActions(['getAlerts', 'getSchedule']),
      ...mapMutations(['setMessagesMoreRows']),
      onDayChange(value) {
        this.getSchedule(value);
      },
      goToDetailsHandler({ alertTypeId }) {
        if (alertTypeId) {
          this.$router.push({
            name: 'alertDetail',
            params: {
              id: alertTypeId,
            },
          });
        }
      },
      downloadDocument(documentId) {
        return _generalService.getDocument(documentId);
      },
      async getMessageData() {
        let messages = (await _service.fetchMessages()).data;

        let immediateMessages = messages.filter(
          (s) =>
            s.messageLocationCode === locationMessageCode.Inicio ||
            s.messageLocationCode === locationMessageCode.InicioConAcuse
        );

        let inboxMessages = _.chunk(
          messages.filter(
            (s) => s.messageLocationCode === locationMessageCode.Lateral
          ),
          3
        );

        try {
          sessionManager.set(sessionKeys.KEY_IMMEDIATE_MESSAGES, immediateMessages);
          sessionManager.set(sessionKeys.KEY_INBOX_MESSAGES, inboxMessages);
          sessionManager.set(sessionKeys.KEY_MESSAGES_LOADED, true);
        } catch (e) {
          sessionManager.clear(sessionKeys.KEY_IMMEDIATE_MESSAGES);
          sessionManager.clear(sessionKeys.KEY_INBOX_MESSAGES);
          sessionManager.clear(sessionKeys.KEY_MESSAGES_LOADED);
        }

        this.immediateMessages = immediateMessages;
        this.inboxMessages = inboxMessages;
      },
      async loadMessages() {
        let messagesLoaded = sessionManager.get(
          sessionKeys.KEY_MESSAGES_LOADED,
          false
        );
        if (!messagesLoaded) {
          await this.getMessageData();
        } else {
          this.inboxMessages = sessionManager.get(
            sessionKeys.KEY_INBOX_MESSAGES,
            '[]'
          );
          this.immediateMessages = sessionManager.get(
            sessionKeys.KEY_IMMEDIATE_MESSAGES,
            '[]'
          );
        }
      },
      getInboxMessages() {
        return this.inboxMessages.length > 0 ? this.inboxMessages[0] : [];
      },
      onMarkedAsRead(messageId) {
        _service.saveReadMessage(messageId);
      },
    },
    created() {
      this.getAlerts();
      this.loadMessages().then(() => {
        if (this.immediateMessages.length > 0) {
          this.$refs._immediateMessagePopupRef.init();
        }
        if (this.inboxMessages.length > 0) {
          this.$refs._messagesInbox.init();
        }
      });
      this.getSchedule();
    },
  };
  </script>
  
  <style scoped lang="scss">
  .tag-badge {
    display: inline-block;
    padding-right: 1em;
    height: 42px;
    background: transparent linear-gradient(90deg, #ffffff 0%, #e8e8e8 100%) 0% 0%
      no-repeat padding-box;
    border-radius: 26px;
    cursor: pointer;
    margin-bottom: 10px;
  }
  
  .dot {
    width: 39px;
    height: 39px;
    border-radius: 50%;
    display: inline-block;
    background: #ffffff 0% 0% no-repeat padding-box;
    box-shadow: 0px 3px 6px #00000029;
    padding-top: 9px;
  }
  .orange-icon {
    color: #ff7058;
    font-size: 26px;
  }
  
  .orange-icon-badge-align {
    vertical-align: top;
    margin-left: -3px;
    margin-top: -3px;
  }
  
  .tag-badge-title {
    text-align: left;
    font: Bold 25px/30px Lato;
    letter-spacing: 0;
    color: #11291b;
    display: inline-block;
  }
  
  .icon-position {
    max-width: 6%;
    padding-top: 0px;
    text-align: center;
  }
  .force-orange {
    color: #ff7058;
  }
  
  .alert-empty-container {
    box-shadow: 0px 3px 6px #00000029;
    border: 0.5px solid #e2e2e2;
    border-radius: 8px;
    padding: 20px;
    margin-top: 40px;
    &__wrapper {
      background: linear-gradient(to top, #fff, #d9e5df 120%);
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 50px 50px 30px 50px;
      em {
        font-size: 70px;
        color: #c5d1cb;
      }
      span {
        font-size: 21px;
        color: #3f5b60;
        margin-top: 20px;
        font-weight: bold;
      }
    }
  }
  </style>
  